import Page from "src/components/Page";
import { yupResolver } from "@hookform/resolvers/yup";
import { Card, CircularProgress } from "@mui/material";
import { Box, Container, Typography } from "@mui/material";
import { FC, useEffect } from "react";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormProvider } from "src/components/hook-form";
import { DEFAULT_FORM_BEHAVIOUR } from "src/config/config";
import { useNavigate, useParams } from "react-router";
import { PATH_USERS } from "src/routes/paths";
import { useSnackbar } from "notistack";
import HomeAccountUpsertCustomerAkaUserForm, {
    emptyHomeCustomerForm,
    homeCustomerFormSchema,
} from "src/modules/customer/components/forms/HomeAccountUpsertCustomerAkaUserForm";
import { useCustomerDetails } from "src/modules/customer/hooks/useCustomerDetails";
import { useCustomerUpdateMutation } from "src/modules/customer/hooks/useUpdateMutation";
import {
    CustomerUpsertRequest,
    useCustomerCreateMutation,
} from "src/modules/customer/hooks/useCreateMutation";
import { CustomerFormShape } from "src/modules/customer/components/forms/UpsertCustomerForm";
import { useQueryClient } from "@tanstack/react-query";
import { RQ_COMPANY_USERS_LIST_KEY } from "src/modules/user/hooks/CompanyUser/useUserList";

const UpsertCustomerAkaUserPage: FC = () => {
    const queryClient = useQueryClient();
    const params = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const {
        data: customer,
        refetch,
        isSuccess: isCustomerSuccess,
        isLoading: isCustomerLoading,
    } = useCustomerDetails(params?.id || "");

    const methods = useForm({
        ...DEFAULT_FORM_BEHAVIOUR,
        defaultValues: emptyHomeCustomerForm(),
        resolver: yupResolver(homeCustomerFormSchema(t)),
    });

    const { handleSubmit, reset } = methods;

    const { mutate: updateCustomer } = useCustomerUpdateMutation({
        onSuccess: () => {
            queryClient.invalidateQueries([RQ_COMPANY_USERS_LIST_KEY]);
            navigate(PATH_USERS);
            enqueueSnackbar(t("user.edit.userUpdated"), { variant: "success" });
        },
    });

    const { mutate: createCustomer } = useCustomerCreateMutation({
        onSuccess: () => {
            queryClient.invalidateQueries([RQ_COMPANY_USERS_LIST_KEY]);
            navigate(PATH_USERS);
            enqueueSnackbar(t("user.edit.userUpdated"), { variant: "success" });
        },
    });

    const handleUpdate = (values: CustomerFormShape) => {
        const converted: CustomerUpsertRequest = {
            ...values,
            active: !!(values.active === "active"),
            creditLimit: null,
            tokens: values.tokens.map((token) => {
                return {
                    ...token,
                    isActive: true,
                    validFrom: null,
                    validTo: null,
                    billingProfile: null,
                };
            }),
            customerGroup: null,
            lang: "pl",
        };
        if (values.id) {
            updateCustomer(converted);
        } else {
            createCustomer({ ...converted });
        }
    };

    useEffect(() => {
        refetch();
    }, []);

    useEffect(() => {
        if (isCustomerSuccess && customer?.id) {
            reset({
                ...customer,
                active: customer.active ? "active" : "disabled",
            });
        }
    }, [customer, isCustomerSuccess]);

    return (
        <Page title={t("user.edit.title")}>
            <Container maxWidth={"xl"}>
                <Box
                    sx={{
                        mb: { xs: 2, sm: 4 },
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", sm: "center" },
                    }}
                >
                    <Typography variant="h3" component="h1">
                        {t("user.edit.title")}
                    </Typography>
                </Box>
                <Card variant="main">
                    {params?.id && isCustomerLoading && <CircularProgress />}
                    {!params.id || (params.id && isCustomerSuccess) ? (
                        <FormProvider
                            methods={methods}
                            onSubmit={handleSubmit(handleUpdate as SubmitHandler<FieldValues>)}
                        >
                            <HomeAccountUpsertCustomerAkaUserForm />
                        </FormProvider>
                    ) : (
                        <></>
                    )}
                </Card>
            </Container>
        </Page>
    );
};

export default UpsertCustomerAkaUserPage;
